import { Box, Chip, Link, Stack, Typography } from "@mui/material"
import { FC, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Link as RouterLink } from "react-router-dom"
import {
  useGetSstv2DlqMessagesQuery,
  useGetSstv2DlqQueuesQuery,
} from "src/features/partners/partnersApi"
import { Sstv2MigrationCallout } from "./Sstv2MigrationCallout"
import {
  INGESTION_QUEUE_NAME_FRAGMENT,
  ingestionDlqState,
} from "./sstv2AttemptState"

/** Matches the lifecycle panel's cadence so the page refreshes as one. */
const POLL_INTERVAL_MS = 20000

/**
 * INGESTION DLQ — surfaced on the migration page, not only on a queue dashboard.
 *
 * ## Why this belongs here
 *
 * Since ingestion became a pure accumulator, the mapping items it writes ARE the
 * flip's work set. So a migration ingest envelope that exhausts its receives and
 * dead-letters means **mapping items that were never written** — and the flip cannot
 * miss what was never recorded. Nothing else in the system can see that:
 *
 *  - the job counters only record what the partner told us;
 *  - stalled-flip detection looks for mappings that EXIST and are unflipped, so a
 *    mapping that does not exist is invisible to it;
 *  - the partner-counter reconcile notices the discrepancy but writes nothing.
 *
 * The reconcile tick re-drives this queue, which recovers the transient cases on its
 * own. What it cannot recover is a message it has GIVEN UP on: past the per-message
 * attempt cap the envelope is left in the DLQ deliberately, and its work is
 * permanently absent from the migration until an operator intervenes. That is the
 * number this panel exists for.
 *
 * ## What it does NOT do
 *
 * No purge, no re-drive button. The DLQ read path is read-only by design
 * (`PurgeQueue` / `StartMessageMoveTask` are deliberately not imported anywhere in
 * `dlqViewer.ts`), and the tick already owns re-driving. This panel reports; the
 * full message bodies live one click away on the DLQ management page.
 *
 * ## It renders NOTHING when the DLQ is empty
 *
 * Which is the normal case, and is why it can sit un-folded above the job table
 * instead of inside a collapsed section. A severe callout behind a fold is the same
 * as no callout — and this signal's entire value is that it appears unbidden, since
 * nothing else in the system will ever mention the work it represents.
 */
export const Sstv2IngestionDlqPanel: FC = () => {
  const { t } = useTranslation()

  const queues = useGetSstv2DlqQueuesQuery(undefined, {
    pollingInterval: POLL_INTERVAL_MS,
    skipPollingIfUnfocused: true,
  })

  // Matched on the QUEUE NAME rather than the partner-configurable tag. Since the
  // second queue was deleted there should be exactly one, but keeping it a filter
  // means an extra SQS config cannot make this panel silently describe the wrong
  // queue.
  const ingestion = useMemo(
    () =>
      (queues.data?.data ?? []).find((q) =>
        q.queueName?.includes(INGESTION_QUEUE_NAME_FRAGMENT),
      ),
    [queues.data],
  )

  const dlqUrl = ingestion?.dlqUrl ?? undefined
  const messages = useGetSstv2DlqMessagesQuery(
    { queueUrl: dlqUrl ?? "" },
    {
      // Only peek when there is something to peek at — an empty DLQ needs no
      // ReceiveMessage call every 20 seconds.
      skip: !dlqUrl || !ingestion?.dlqDepth,
      pollingInterval: POLL_INTERVAL_MS,
      skipPollingIfUnfocused: true,
    },
  )

  const state = useMemo(
    () =>
      ingestionDlqState(ingestion?.dlqDepth ?? null, messages.data?.data ?? []),
    [ingestion?.dlqDepth, messages.data],
  )

  // Silent while loading: this panel is an exception reporter, and a spinner above
  // the job table on every page load would train operators to ignore the space.
  if (queues.isLoading) return null

  // "Cannot read the queue" is NOT "the queue is clean", so it is still reported —
  // but as advisory, because the likely cause is a missing SQS config rather than
  // lost migration work.
  if (queues.isError || !ingestion) {
    return (
      <Box sx={{ mb: 3 }}>
        <Sstv2MigrationCallout
          severity="advisory"
          title={t("migrationDlqUnavailable")}
          hint={t("migrationDlqUnavailableHint")}
        />
      </Box>
    )
  }

  const depth = state.depth ?? 0

  // The normal case: nothing to say, so nothing is said.
  if (depth === 0) return null

  return (
    <Stack data-ui="UI-SL2W-255" spacing={2} sx={{ mb: 3 }}>
      {/* Abandoned work: the tick has stopped retrying these, so nothing else
          will. Severe even at a count of one — one lost envelope is a batch of
          reservations missing from the flip's work set. */}
      {state.exhausted > 0 && (
        <Sstv2MigrationCallout
          severity="severe"
          title={t("migrationDlqExhausted", { count: state.exhausted })}
          hint={t("migrationDlqExhaustedHint")}
        />
      )}

      {/* Present but under the cap: the tick will handle it. Worth knowing,
          not worth acting on. */}
      {state.exhausted === 0 && (
        <Sstv2MigrationCallout
          severity="advisory"
          title={t("migrationDlqNonEmpty", { count: depth })}
          hint={t("migrationDlqNonEmptyHint")}
        />
      )}

      <Stack spacing={1.25}>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t("migrationDlqDepth")}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" fontFamily="monospace" fontWeight={700}>
              {depth}
            </Typography>
            <Chip
              size="small"
              variant="outlined"
              label={ingestion.dlqName ?? t("migrationDlqUnnamed")}
              sx={{ height: 20, fontSize: "0.68rem" }}
            />
          </Stack>
        </Stack>

        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t("migrationDlqRetrying")}
          </Typography>
          <Typography variant="body2" fontFamily="monospace">
            {state.retrying}
          </Typography>
        </Stack>

        {/* The peek is capped, so `exhausted` is a LOWER BOUND whenever the DLQ
              is deeper than the peek. Said out loud, because "0 abandoned"
              computed over 10 of 4,000 messages would read as reassurance. */}
        {state.peekTruncated && (
          <Typography variant="caption" color="text.secondary">
            {t("migrationDlqPeekTruncated", {
              peeked: messages.data?.data?.length ?? 0,
              depth,
            })}
          </Typography>
        )}

        <Box>
          {/* tab=7 is the DLQ viewer on /sa/sstv2 (SaSstv2Page's TabMap) — the
                same index-in-a-query-param convention the drawer's trace link
                uses. That page shows the full envelope bodies; this panel only
                answers "is migration work being lost". */}
          <Link component={RouterLink} to="/sa/sstv2?tab=7" variant="caption">
            {t("migrationDlqViewMessages")}
          </Link>
        </Box>
      </Stack>
    </Stack>
  )
}

export default Sstv2IngestionDlqPanel
